import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, SITE_URL, absUrl } from "@/lib/seo";
import { internalPath } from "@/lib/paths";
import { withBookingUtm } from "@/lib/booking";

const BOOK_URL = withBookingUtm(
  "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678",
);
const CANONICAL = `${SITE_URL}/barbershop-stockholm`;

const visitSteps = [
  {
    t: "1. Konsultation",
    d: "Barberaren går igenom hårtyp, hårväxt och vad du vill ha. Har du en referensbild är den till stor hjälp – begrepp som fade, taper och skin fade betyder olika saker för olika personer.",
  },
  {
    t: "2. Klippning",
    d: "Sax, maskin och rakkniv används tillsammans. En fade byggs upp i flera steg där längderna blandas ut mot varandra tills övergången är osynlig.",
  },
  {
    t: "3. Skägg och linjering",
    d: "Skägget formas efter ansiktsform och kanterna linjeras med rakkniv. Varm handduk och rakolja hör till det klassiska hantverket.",
  },
  {
    t: "4. Styling och råd",
    d: "Håret stylas färdigt och du får råd om produkt, teknik och när det är dags att komma tillbaka – oftast var tredje till femte vecka för en fade.",
  },
];

const included = [
  "Konsultation innan klippningen",
  "Klippning med sax och maskin",
  "Nackrakning och linjering med rakkniv",
  "Tvätt och styling",
  "Produktråd anpassade efter din hårtyp",
];

const faq = [
  {
    q: "Vad är en barbershop?",
    a: "En barbershop är en salong specialiserad på herrgrooming – klippning, fade, skäggformning och rakning. Barberaren arbetar med sax, maskin och rakkniv och är utbildad i tekniker som främst används på kortare herrfrisyrer.",
  },
  {
    q: "Vad är skillnaden mellan en barbershop och en frisörsalong?",
    a: "En frisörsalong klipper alla och arbetar ofta mycket med längre hår, färg och behandlingar. En barbershop fokuserar på herr och på tekniker som fade, taper, linjering och rakknivrakning. Väljer du barbershop får du en barberare som gör den typen av klippning varje dag.",
  },
  {
    q: "Hur går ett besök på en barbershop till?",
    a: "Först en kort konsultation om längder och form, sedan klippning med sax och maskin, därefter skägg och linjering med rakkniv och till sist tvätt, styling och produktråd. Ett besök tar oftast 30–45 minuter.",
  },
  {
    q: "Vad ingår i en herrklippning på en barbershop?",
    a: "Konsultation, klippning, nackrakning och linjering, tvätt och styling. Skäggtrimning är en egen tjänst men bokas ofta i kombination med klippningen.",
  },
  {
    q: "Vad kostar det att gå till en barbershop i Stockholm?",
    a: "Priset ligger vanligtvis mellan 350 och 550 kr för en herrklippning i Stockholm. Hos Tailor's Barber kostar herrklippning 450 kr och skäggtrimning 380 kr.",
  },
  {
    q: "Hur ofta bör man gå till barberaren?",
    a: "En fade eller kortare frisyr håller formen i ungefär tre till fyra veckor. Har du längre hår eller mjukare övergångar räcker det ofta med var sjätte till åttonde vecka.",
  },
  {
    q: "Vad ska jag säga till barberaren?",
    a: "Beskriv hur kort du vill ha på sidorna, hur hög övergången ska börja och hur mycket längd du vill behålla på toppen. Att visa en bild är alltid enklast – begreppen tolkas olika.",
  },
  {
    q: "Kan man komma på drop-in till en barbershop?",
    a: "Ja, de flesta barbershops tar emot drop-in i mån av tid. Vill du vara säker på en tid, särskilt på helger och kvällar, är onlinebokning tryggare.",
  },
];

export const Route = createFileRoute("/barbershop-stockholm")({
  head: () => ({
    meta: [
      { title: "Barbershop Stockholm: guide, priser & tjänster | Tailor's Barber" },
      {
        name: "description",
        content:
          "Barbershop Stockholm: vad en barbershop är, vad det kostar och vilka tjänster du får. Tailor's Barber har fyra barbershops i Stockholm. Boka online.",
      },
      {
        property: "og:title",
        content: "Barbershop Stockholm: guide, priser & tjänster | Tailor's Barber",
      },
      {
        property: "og:description",
        content:
          "Barbershop Stockholm: vad en barbershop är, vad det kostar och vilka tjänster du får. Tailor's Barber har fyra barbershops i Stockholm. Boka online.",
      },
      { property: "og:url", content: absUrl(CANONICAL) },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: absUrl(CANONICAL) }],
    scripts: [
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Barbershop Stockholm", url: "/barbershop-stockholm" },
        ]),
      ),
      jsonLd(faqSchema(faq)),
    ],
  }),
  component: BarbershopStockholmPage,
});

function BarbershopStockholmPage() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-6">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">
            Hem
          </Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Barbershop Stockholm</span>
        </nav>
        <div className="eyebrow">Guide</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Vad är en barbershop?
        </h1>
        <p className="mt-5 text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En barbershop Stockholm är en salong som specialiserar sig på herrgrooming: klippning,
          skägg, rakning och styling.{" "}
          <Link to={internalPath("/barberare-stockholm")} className="underline">
            Tailor&apos;s Barber är en sådan barbershop i Stockholm
          </Link>
          , med fyra salonger och barberare som fokuserar på herrklippning, fade och skäggtrimning.
          Här går vi igenom vad en barbershop i Stockholm är, vad som ingår i ett besök och varför
          allt fler herrar väljer barberare framför frisör.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Barbershop eller frisörsalong: vad är skillnaden?</h2>
        <div className="mt-4 space-y-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          <p>
            En barbershop (på svenska barbersalong eller herrfrisör) är en salong som specialiserar
            sig på herrgrooming: klippning, skägg, rakning och styling. En frisörsalong klipper alla
            och arbetar i högre grad med längre hår, färg och behandlingar.
          </p>
          <p>
            Den praktiska skillnaden ligger i teknikerna. Barberaren arbetar dagligen med maskin och
            rakkniv och bygger graderade övergångar – fade, taper och skin fade – där längderna
            blandas ut mot varandra tills skarven inte syns. Det är ett hantverk som kräver
            repetition, och en barbershop gör i praktiken inget annat.
          </p>
          <p>
            Skäggarbetet skiljer sig också. På en barbershop formas skägget efter ansiktsform,
            kanterna linjeras med rakkniv och avslutas ofta med varm handduk och rakolja – ett
            moment som sällan ingår i en vanlig frisörklippning.
          </p>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Så går ett besök till</h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {visitSteps.map((s) => (
            <div key={s.t} className="card-elev p-6">
              <h3 className="text-lg md:text-xl">{s.t}</h3>
              <p className="mt-2 text-foreground/80 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Ett vanligt besök tar 30–45 minuter. Bokar du klippning och skägg tillsammans bör du räkna
          med runt en timme.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vad ingår i en klippning?</h2>
        <ul className="mt-5 grid gap-2 max-w-2xl text-base md:text-lg text-foreground/80">
          {included.map((i) => (
            <li key={i} className="card-elev px-5 py-3">
              {i}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En herrklippning i Stockholm kostar oftast mellan 350 och 550 kr, och skäggtrimning något
          mindre. Läs mer om enskilda tekniker i guiderna om{" "}
          <Link to={internalPath("/taper-fade")} className="underline">
            taper fade
          </Link>
          ,{" "}
          <Link to={internalPath("/skin-fade")} className="underline">
            skin fade
          </Link>{" "}
          och{" "}
          <Link to={internalPath("/skaggtrimning")} className="underline">
            skäggtrimning
          </Link>
          , eller se{" "}
          <Link to={internalPath("/priser")} className="underline">
            våra priser
          </Link>
          .
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Varför väljer herrar barbershop?</h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {[
            {
              t: "Specialisering framför bredd",
              d: "Barberaren gör samma typ av klippning varje dag. Det märks framför allt i övergångarna och i kanterna.",
            },
            {
              t: "Rakkniven som verktyg",
              d: "Nacke, kanter och skäggkonturer blir skarpare med rakkniv än med maskin, och resultatet håller längre.",
            },
            {
              t: "Kortare, tätare besök",
              d: "En kort frisyr växer ur formen snabbt. Barbershopen är byggd för återkommande besök var tredje till femte vecka.",
            },
            {
              t: "Rådgivning som är konkret",
              d: "Du får veta vilken längd som fungerar med din hårväxt, vilken produkt som passar och när du bör komma tillbaka.",
            },
          ].map((b) => (
            <div key={b.t} className="card-elev p-6">
              <h3 className="text-lg md:text-xl">{b.t}</h3>
              <p className="mt-2 text-foreground/80 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <Faq items={faq} heading="Vanliga frågor om barbershops" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Vill du boka en barbershop i Stockholm?</h2>
          <p className="mt-3 text-foreground/80 max-w-2xl mx-auto">
            Tailor&apos;s Barber finns på flera platser i Stockholmsregionen. Välj salong på
            startsidan så ser du öppettider, adress och lediga tider.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg">
              <Link to="/">Se våra salonger</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">
                Boka tid online
              </a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
